import React, { useCallback, useMemo, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import BotoneraModern from "../../components/Botonera/BotoneraModern.tsx";
import Breadcrumbs from "../../components/UI/Breadcrumbs/Breadcrumbs.tsx";
import PeopleSearchLayout from "../../components/PeopleSearch/PeopleSearchLayout.tsx";
import PeopleSearchHero from "../../components/PeopleSearch/PeopleSearchHero.tsx";
import PeopleSearchSearchBarWithDropdown, {
  SearchSuggestion,
} from "../../components/PeopleSearch/PeopleSearchSearchBarWithDropdown.tsx";
import { useSearchHistory } from "../../hooks/useSearchHistory.ts";
import layoutStyles from "../../components/PeopleSearch/PeopleSearchLayout.module.scss";
import PeopleSearchFilters from "../../components/PeopleSearch/PeopleSearchFilters.tsx";
import PeopleSearchResults from "../../components/PeopleSearch/PeopleSearchResults.tsx";
import useNavVisibility from "../../hooks/useNavVisibility.ts";
import useProfesionalesBootstrap from "../../hooks/useProfesionalesBootstrap.ts";
import useProfesionalesFilters from "../../hooks/useProfesionalesFilters.ts";
import { useActionGuard } from "../../hooks/useActionGuard.ts";
import {
  selectFilteredMeta,
  selectFilteredUsers,
  selectJurisdictions,
  selectFeaturedUsers,
  selectSkillsList,
} from "../../store/selectors/profesionales.ts";
import { fetchFilteredProfessionals } from "../../store/profesionales/thunks.ts";
import { sanitizeProfessionals, profesionalesService, UserFilterRol } from "../../services/profesionalesService.ts";
import { interpretSearch } from "../../services/llmService.ts";
import {
  PeopleSearchProfile,
  PeopleSearchTypeOption,
  PeopleSearchProfileType,
} from "../../types/peopleSearch.ts";
import { openSendContactNotificationModal } from "../../store/actions";
import * as actionTypes from "../../store/actions/types";
import { buildCatalogSuggestions } from "../../utils/buildCatalogSuggestions.ts";

type SkillOption = {
  id: number;
  name: string;
};

const DEFAULT_PAGE_SIZE = 12;

const buildDisplayName = (professional: any) => {
  if (professional.type === "COMPANY") {
    return professional.razon_social ?? professional.name ?? "";
  }
  const name = professional.name ?? "";
  const lastName = professional.last_name ?? "";
  return `${name} ${lastName}`.trim() || "Profesional";
};

const getSuggestionDisplayName = (user: any) => {
  if (user?.rols?.some((r: any) => r?.name === "JOBBOARD")) {
    const name = user.name ?? user.jobBoard?.name ?? "";
    const last = user.last_name ?? user.jobBoard?.last_name ?? "";
    const full = `${name} ${last}`.trim();
    return full || (user.email ?? user.jobBoard?.email ?? "Trabajador");
  }
  return buildDisplayName(user);
};

const SUGGESTIONS_DEBOUNCE_MS = 250;
const MIN_CHARS_FOR_SUGGESTIONS = 3;
const MIN_CHARS_FOR_IA_SUGGESTIONS = 2;

const mapProfessionalToProfile = (
  professional: any,
  source: "FEATURED" | "FILTERED" = "FILTERED"
): PeopleSearchProfile => {
  const rawType = professional.type === "COMPANY" ? "COMPANY" : "PERSON";
  const type: PeopleSearchProfileType = source === "FEATURED"
    ? "FEATURED"
    : rawType === "COMPANY"
      ? "PROFESSIONAL_COMPANY"
      : "PROFESSIONAL_PERSON";

  const skillsSource = professional.skill ?? professional.skills ?? [];
  const skills = skillsSource
    .map((item: any) => {
      const entity = item?.skill ?? item;
      if (!entity?.id) return null;
      return {
        id: Number(entity.id),
        name: String(entity.name ?? ""),
      };
    })
    .filter((skill: any) => skill && !Number.isNaN(skill.id));

  const jurisdictions: Array<{ id: number; name: string }> = [];
  if (professional.zone) {
    jurisdictions.push({ id: 0, name: professional.zone });
  }
  (professional.jobBoardZones ?? []).forEach((zone: any) => {
    const entity = zone?.jurisdiccion ?? {};
    const id = Number(entity?.id ?? zone?.id);
    const name = String(entity?.name ?? zone?.city ?? "");
    if (!Number.isNaN(id) && name && !jurisdictions.some((j) => j.id === id)) {
      jurisdictions.push({ id, name });
    }
  });

  return {
    id: String(professional.id),
    type,
    displayName: buildDisplayName(professional),
    companyName: professional.razon_social,
    avatarUrl: professional.image_profile ?? null,
    skills,
    jurisdictions,
    contact: {
      phone: professional.phone,
      email: professional.username ?? professional.email,
      website: professional.url_site_web,
    },
    tags: [],
    rating: professional.stars ?? null,
    portfolioCount: professional.portfolio?.length ?? 0,
    availability: null,
    unemploymentFund: undefined,
    hasIdentityValidated: Boolean(professional.validated ?? true),
    rawType,
    source,
    raw: professional,
  };
};

const isWorkerByRol = (user: any) =>
  user?.rols?.some((r: any) => r?.name === "JOBBOARD") ?? false;

const mapWorkerToProfile = (worker: any): PeopleSearchProfile => {
  const skillsSource = worker.skill ?? worker.skills ?? [];
  const skills = skillsSource
    .map((item: any) => {
      const entity = item?.skill ?? item;
      if (!entity?.id) return null;
      return { id: Number(entity.id), name: String(entity.name ?? "") };
    })
    .filter((s: any) => s && !Number.isNaN(s.id));

  const jurisdictions: Array<{ id: number; name: string }> = [];
  (worker.jobBoardZones ?? []).forEach((zone: any) => {
    const entity = zone?.jurisdiccion ?? {};
    const id = Number(entity?.id ?? zone?.id);
    const name = String(entity?.name ?? zone?.city ?? "");
    if (!Number.isNaN(id) && name && !jurisdictions.some((j) => j.id === id)) {
      jurisdictions.push({ id, name });
    }
  });

  const displayName =
    `${worker.name ?? ""} ${worker.last_name ?? ""}`.trim() ||
    worker.razon_social ||
    worker.email ||
    "Trabajador";

  return {
    id: String(worker.id),
    type: "WORKER",
    displayName,
    avatarUrl: worker.image_profile ?? null,
    skills,
    jurisdictions,
    contact: {
      phone: worker.phone ?? null,
      email: worker.email ?? worker.username ?? null,
      website: worker.url_site_web ?? null,
    },
    tags: [],
    rating: null,
    portfolioCount: undefined,
    availability: worker.employment_end_date
      ? new Date(worker.employment_end_date) <= new Date()
        ? "Disponibilidad inmediata"
        : `Disponible desde ${new Date(worker.employment_end_date).toLocaleDateString("es-AR")}`
      : null,
    unemploymentFund: Boolean(worker.unemployment_fund),
    hasIdentityValidated: Boolean(worker.identity_validated ?? false),
    rawType: "WORKER",
    source: "WORKER",
    raw: worker,
  };
};

const mapUserToProfile = (user: any, source: "FEATURED" | "FILTERED" = "FILTERED"): PeopleSearchProfile =>
  isWorkerByRol(user) ? mapWorkerToProfile(user) : mapProfessionalToProfile(user, source);

const PeopleSearchPage = () => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const { guard } = useActionGuard();
  const { ocultarEnMobile, mobileStylesFromParent } = useNavVisibility();
const { isLoading, jurisdictionsLoaded } = useProfesionalesBootstrap();

  const featuredUsers = useSelector(selectFeaturedUsers) as any[];
  const skillsList = useSelector(selectSkillsList) as any[];
  const jurisdictionsRaw = useSelector(selectJurisdictions) as any[];
  const filteredUsers = useSelector(selectFilteredUsers) as any[];
  const filteredMeta = useSelector(selectFilteredMeta) as any;

  const sanitizedFeaturedUsers = useMemo(
    () => sanitizeProfessionals(featuredUsers),
    [featuredUsers]
  );

  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const suggestionsDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchHistory = useSearchHistory();
  const [typeFilterState, _setTypeFilter] = useState<PeopleSearchTypeOption>("ALL");
  const typeFilterRef = useRef<PeopleSearchTypeOption>("ALL");
  const setTypeFilter = useCallback((nextType: PeopleSearchTypeOption) => {
    typeFilterRef.current = nextType;
    _setTypeFilter(nextType);
  }, []);
  const exitFeaturedMode = useCallback(() => {
    if (typeFilterRef.current === "FEATURED") {
      typeFilterRef.current = "ALL";
      _setTypeFilter("ALL");
    }
  }, [_setTypeFilter]);
  const typeFilter = typeFilterState;
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const verifiedOnlyRef = useRef(verifiedOnly);
  verifiedOnlyRef.current = verifiedOnly;
  const [heroCollapsed, setHeroCollapsed] = useState(false);
  const [interpretSearchLabel, setInterpretSearchLabel] = useState<string | null>(null);
  const [interpretSkillLabels, setInterpretSkillLabels] = useState<Array<{ id: number; name: string }>>([]);
  const [interpretJurisdictionLabels, setInterpretJurisdictionLabels] = useState<Array<{ id: number; name: string }>>([]);
  const [useInterpretSearch, setUseInterpretSearch] = useState(false);

  const getRolForTypeFilter = useCallback((t: PeopleSearchTypeOption): UserFilterRol => {
    if (t === "WORKER") return "JOBBOARD";
    if (t === "ALL") return "ALL";
    return "CLIENT";
  }, []);

  const handleFilterApply = useCallback(
    ({ skillIds, jurisdictionIds, allCountry, page, name: nameTerm, featured: featuredFilter }) => {
      const hasFilters =
        (skillIds?.length ?? 0) > 0 ||
        (jurisdictionIds?.length ?? 0) > 0 ||
        Boolean(allCountry) ||
        Boolean(featuredFilter) ||
        (nameTerm?.trim?.()?.length ?? 0) > 0 ||
        verifiedOnlyRef.current;
      const currentType = typeFilterRef.current;
      if (hasFilters && currentType === "FEATURED") {
        exitFeaturedMode();
      }
      const effectiveType = hasFilters && currentType === "FEATURED" ? "ALL" : currentType;
      if (effectiveType === "FEATURED") {
        return;
      }
      const typeParam = effectiveType === "PROFESSIONAL"
        ? "PERSON"
        : effectiveType === "COMPANY"
          ? "COMPANY"
          : undefined;
      const rol = getRolForTypeFilter(effectiveType);
      dispatch(
        fetchFilteredProfessionals({
          skillIds,
          jurisdictionIds,
          allCountry,
          page,
          pageSize: DEFAULT_PAGE_SIZE,
          type: typeParam,
          rol,
          identityValidated: verifiedOnlyRef.current ? true : undefined,
          featured: featuredFilter ? true : undefined,
          name: nameTerm?.trim() || undefined,
        })
      );
    },
    [dispatch, exitFeaturedMode, getRolForTypeFilter]
  );


  const {
    state: filterState,
    actions: filterActions,
    meta: filtersMeta,
  } = useProfesionalesFilters({
    onApply: handleFilterApply,
    initialPage: filteredMeta?.page || 1,
  });

  const handleSearchCommit = useCallback(
    async (term: string) => {
      exitFeaturedMode();
      setHeroCollapsed(true);
      const trimmed = term.trim();
      if (!trimmed) return;
      if (!useInterpretSearch) {
        filterActions.setName(trimmed);
        setInterpretSearchLabel(null);
        setInterpretSkillLabels([]);
        setInterpretJurisdictionLabels([]);
        return;
      }
      dispatch({ type: actionTypes.START_LOADING });
      try {
        const result = await interpretSearch(trimmed);
        const p = result.params;
        const typeFromParams = p.type === "COMPANY" ? "COMPANY" : p.type === "PERSON" ? "PROFESSIONAL" : null;
        const rol = (p.rol || "ALL") as UserFilterRol;
        const typeFromRol = rol === "JOBBOARD" ? "WORKER" : "ALL";
        const typeFilterValue = typeFromParams ?? typeFromRol;
        setTypeFilter(typeFilterValue);
        const identityValidated = p.identity_validated ?? false;
        setVerifiedOnly(identityValidated);
        verifiedOnlyRef.current = identityValidated;
        filterActions.initialize({
          skillIds: p.skillIds ?? [],
          jurisdictionIds: p.jurisdictionIds ?? [],
          allCountry: p.allCountry ?? false,
          page: p.page ?? 1,
          name: p.search ?? "",
          featured: p.featured ?? false,
        });
        if (result.matchType === "similar" && result.searchLabel) {
          setInterpretSearchLabel(result.searchLabel);
        } else {
          setInterpretSearchLabel(null);
        }
        setInterpretSkillLabels(result.skillLabels ?? []);
        setInterpretJurisdictionLabels(result.jurisdictionLabels ?? []);
      } catch {
        setInterpretSearchLabel(null);
        setInterpretSkillLabels([]);
        setInterpretJurisdictionLabels([]);
        filterActions.setName(trimmed);
        dispatch({ type: actionTypes.END_LOADING });
      }
    },
    [dispatch, exitFeaturedMode, filterActions, setTypeFilter, useInterpretSearch]
  );

  const featuredProfiles = useMemo(
    () => (sanitizedFeaturedUsers ?? []).filter(Boolean).map((professional) => mapProfessionalToProfile(professional, "FEATURED")),
    [sanitizedFeaturedUsers]
  );

  const filteredProfiles = useMemo(
    () => (filteredUsers ?? []).filter(Boolean).map((user) => mapUserToProfile(user, "FILTERED")),
    [filteredUsers]
  );

  const [skillCatalog, setSkillCatalog] = useState<SkillOption[]>([]);

  const hasFilterSelections =
    filterState.skillIds.length > 0 ||
    filterState.jurisdictionIds.length > 0 ||
    filterState.allCountry ||
    filterState.featured ||
    (filterState.name?.trim?.()?.length ?? 0) > 0;

  const showFeatured = typeFilter === "FEATURED" && !hasFilterSelections && !verifiedOnly;
  useEffect(() => {
    if ((hasFilterSelections || verifiedOnly) && typeFilterRef.current === "FEATURED") {
      exitFeaturedMode();
    }
  }, [hasFilterSelections, verifiedOnly, exitFeaturedMode]);

  useEffect(() => {
    let isActive = true;
    const loadCatalog = async () => {
      try {
        const catalog = await profesionalesService.fetchSkillCatalog();
        const normalized = catalog
          .map((item) => ({
            id: item.id,
            name: item.name,
          }))
          .sort((a, b) => a.name.localeCompare(b.name));
        if (isActive) {
          setSkillCatalog(normalized);
        }
      } catch (error) {
        console.error("fetchSkillCatalog", error);
      }
    };
    loadCatalog();
    return () => {
      isActive = false;
    };
  }, []);

  const workerProfiles = useMemo(
    () => filteredProfiles.filter((p) => p.type === "WORKER"),
    [filteredProfiles]
  );

  const popularSkills = useMemo(
    () =>
      skillsList
        .map((skill: any) => ({
          id: Number(skill.id),
          name: typeof skill.name === "string" ? skill.name : String(skill.name ?? ""),
          users: typeof skill.users === "number" ? skill.users : 0,
        }))
        .sort((a, b) => {
          const diff = b.users - a.users;
          if (diff !== 0) return diff;
          return a.name.localeCompare(b.name);
        })
        .map(({ id, name }) => ({ id, name })),
    [skillsList]
  );

  const baseSkillOptions = useMemo(
    () => (skillCatalog.length > 0 ? skillCatalog : popularSkills),
    [skillCatalog, popularSkills]
  );

  const skillOptions = useMemo(() => baseSkillOptions, [baseSkillOptions]);

  const baseJurisdictionOptions = useMemo(() => {
    const map = new Map<number, string>();
    jurisdictionsRaw.forEach((item: any, index: number) => {
      const rawId = item.id ?? item.jurisdiccion_id ?? item.jurisdiccion?.id ?? index;
      const id = Number(rawId);
      const name = item.jurisdiccion_name ?? item.name ?? item.jurisdiccion?.name ?? "Jurisdicción";

      if (!map.has(Number.isNaN(id) ? index : id)) {
        map.set(Number.isNaN(id) ? index : id, String(name));
      }
    });
    return Array.from(map.entries())
      .map(([id, name]) => ({
        id,
        name,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [jurisdictionsRaw]);

  const professionalProfiles = useMemo(
    () => filteredProfiles.filter((p) => p.type !== "WORKER"),
    [filteredProfiles]
  );

  const jurisdictionOptions = useMemo(
    () => baseJurisdictionOptions,
    [baseJurisdictionOptions]
  );

  useEffect(() => {
    const trimmed = searchQuery.trim();
    const minChars = useInterpretSearch ? MIN_CHARS_FOR_IA_SUGGESTIONS : MIN_CHARS_FOR_SUGGESTIONS;
    if (!trimmed || trimmed.length < minChars) {
      setSuggestions([]);
      setSuggestionsLoading(false);
      if (suggestionsDebounceRef.current) {
        clearTimeout(suggestionsDebounceRef.current);
        suggestionsDebounceRef.current = null;
      }
      return;
    }
    if (useInterpretSearch) {
      setSuggestions(
        buildCatalogSuggestions(trimmed, baseSkillOptions, baseJurisdictionOptions)
      );
      return;
    }
    if (suggestionsDebounceRef.current) clearTimeout(suggestionsDebounceRef.current);
    suggestionsDebounceRef.current = setTimeout(async () => {
      suggestionsDebounceRef.current = null;
      setSuggestionsLoading(true);
      try {
        const typeParam =
          typeFilter === "PROFESSIONAL"
            ? "PERSON"
            : typeFilter === "COMPANY"
              ? "COMPANY"
              : undefined;
        const rol = getRolForTypeFilter(typeFilter === "FEATURED" ? "ALL" : typeFilter);
        const { users } = await profesionalesService.fetchFilteredProfessionals({
          name: trimmed,
          rol,
          type: typeParam,
          pageSize: 8,
          page: 1,
        });
        const items: SearchSuggestion[] = (users ?? []).map((u: any) => ({
          id: u.id ?? u.jobBoard?.id ?? "",
          displayName: getSuggestionDisplayName(u),
        }));
        setSuggestions(items);
      } catch {
        setSuggestions([]);
      } finally {
        setSuggestionsLoading(false);
      }
    }, SUGGESTIONS_DEBOUNCE_MS);
    return () => {
      if (suggestionsDebounceRef.current) clearTimeout(suggestionsDebounceRef.current);
    };
  }, [searchQuery, typeFilter, getRolForTypeFilter, useInterpretSearch, baseSkillOptions, baseJurisdictionOptions]);

  const professionalDisplayResults = useMemo(() => {
    if (showFeatured) return featuredProfiles;
    if (typeFilter === "PROFESSIONAL") {
      return professionalProfiles.filter((p) => p.rawType !== "COMPANY");
    }
    if (typeFilter === "COMPANY") {
      return professionalProfiles.filter((p) => p.rawType === "COMPANY");
    }
    return professionalProfiles;
  }, [showFeatured, typeFilter, featuredProfiles, professionalProfiles]);

  const combinedResults = useMemo(() => {
    if (showFeatured) return featuredProfiles;
    if (typeFilter === "WORKER") return workerProfiles;
    if (typeFilter === "ALL") return filteredProfiles;
    return professionalDisplayResults;
  }, [showFeatured, typeFilter, featuredProfiles, workerProfiles, filteredProfiles, professionalDisplayResults]);

  const displayedProfiles = useMemo(
    () =>
      combinedResults.filter((profile) => {
        if (verifiedOnly && !profile.hasIdentityValidated) {
          return false;
        }
        return true;
      }),
    [combinedResults, verifiedOnly]
  );

  const totalCount = showFeatured
    ? featuredProfiles.length
    : (filteredMeta?.itemCount ?? combinedResults.length);

  const pageForPagination = filteredMeta?.page || filterState.page;

  const usesRemotePagination = !showFeatured;

  const handlePageChange = useCallback((nextPage: number) => {
    filterActions.setPage(nextPage);
    setHeroCollapsed(true);
  }, [filterActions]);

  const buildProfileNavigationQuery = useCallback(() => {
    const params = new URLSearchParams();
    params.set("from", "profesionales");
    if (filterState.skillIds[0]) {
      params.set("skill", String(filterState.skillIds[0]));
    }
    if (filterState.jurisdictionIds.length > 0) {
      params.set("jurisdictions", filterState.jurisdictionIds.join(","));
    }
    if (filterState.allCountry) {
      params.set("allCountry", "1");
    }
    if (pageForPagination > 1) {
      params.set("page", String(pageForPagination));
    }
    return params.toString();
  }, [
    filterState.allCountry,
    filterState.jurisdictionIds,
    filterState.skillIds,
    pageForPagination,
  ]);

  const handleViewProfile = (profile: PeopleSearchProfile) => {
    if (profile.type === "WORKER") return;
    guard("view_profile", () => {
      const baseUrl = `/perfil/${profile.id}`;
      const query = buildProfileNavigationQuery();
      navigate(query ? `${baseUrl}?${query}` : baseUrl);
    })();
  };

  const handleContact = useCallback(
    (profile: PeopleSearchProfile) => {
      if (profile.type === "WORKER") {
    if (profile.contact.email) {
      window.open(`mailto:${profile.contact.email}`, "_blank");
      return;
    }
    if (profile.contact.phone) {
      window.open(`tel:${profile.contact.phone}`);
    }
        return;
      }
      const userData = profile.raw ?? profile;
      guard("send_message", () => {
        dispatch(openSendContactNotificationModal(userData));
      })();
    },
    [dispatch, guard]
  );

  const clearInterpretLabels = useCallback(() => {
    setInterpretSearchLabel(null);
    setInterpretSkillLabels([]);
    setInterpretJurisdictionLabels([]);
  }, []);

  const handleSkillToggle = (id: number) => {
    exitFeaturedMode();
    setHeroCollapsed(true);
    clearInterpretLabels();
    filterActions.toggleSkill(id);
  };

  const handleJurisdictionToggle = (id: number) => {
    exitFeaturedMode();
    setHeroCollapsed(true);
    clearInterpretLabels();
    filterActions.toggleJurisdiction(id);
  };

  const handleToggleAllCountry = () => {
    exitFeaturedMode();
    setHeroCollapsed(true);
    clearInterpretLabels();
    filterActions.toggleAllCountry();
  };

  const handleToggleVerified = () => {
    exitFeaturedMode();
    setHeroCollapsed(true);
    clearInterpretLabels();
    const next = !verifiedOnly;
    verifiedOnlyRef.current = next;
    setVerifiedOnly(next);
    filterActions.applyFilters();
  };

  const handleSearchClear = useCallback(() => {
    setSearchQuery("");
    filterActions.setName("");
  }, [filterActions]);

  const handleClearFilters = useCallback(() => {
    setHeroCollapsed(true);
    setVerifiedOnly(false);
    verifiedOnlyRef.current = false;
    setTypeFilter("ALL");
    setSearchQuery("");
    setInterpretSearchLabel(null);
    setInterpretSkillLabels([]);
    setInterpretJurisdictionLabels([]);
    filterActions.clearFilters();
  }, [filterActions, setTypeFilter]);

  const prevTypeRef = useRef<PeopleSearchTypeOption | null>(null);
  useEffect(() => {
    if (prevTypeRef.current === typeFilter) return;
    prevTypeRef.current = typeFilter;
    if (typeFilter === "WORKER" || typeFilter === "ALL") {
      filterActions.applyFilters();
    }
  }, [typeFilter, filterActions]);

  return (
    <PeopleSearchLayout
      navigation={null}
      mobileNavigation={ocultarEnMobile ? <BotoneraModern mobileStylesFromParent={mobileStylesFromParent} /> : null}
      breadcrumbs={<Breadcrumbs />}
      hero={
        <PeopleSearchHero
          isCompact={heroCollapsed}
          searchBar={
            <PeopleSearchSearchBarWithDropdown
              value={searchQuery}
              onChange={setSearchQuery}
              onSearchCommit={handleSearchCommit}
              useInterpretSearch={useInterpretSearch}
              onUseInterpretSearchChange={(active) => {
                setUseInterpretSearch(active);
                setSuggestions([]);
                setSuggestionsLoading(false);
                if (suggestionsDebounceRef.current) {
                  clearTimeout(suggestionsDebounceRef.current);
                  suggestionsDebounceRef.current = null;
                }
              }}
              suggestions={suggestions}
              suggestionsLoading={suggestionsLoading}
              history={searchHistory.history}
              onAddToHistory={(term) => searchHistory.addToHistory(term, useInterpretSearch)}
              onClearHistory={searchHistory.clearHistory}
              placeholder="Buscar por nombre o empresa…"
              size={heroCollapsed ? "default" : "large"}
              minCharsForSuggestions={useInterpretSearch ? MIN_CHARS_FOR_IA_SUGGESTIONS : MIN_CHARS_FOR_SUGGESTIONS}
            />
          }
        />
      }
      heroClassName={heroCollapsed ? layoutStyles.heroCollapsed : undefined}
      filters={
        <PeopleSearchFilters
          filterCount={filtersMeta.filtersCount + (typeFilter !== "ALL" ? 1 : 0)}
          totalCount={totalCount}
          type={typeFilter}
          popularSkills={popularSkills}
          allSkills={skillOptions}
          jurisdictions={jurisdictionOptions}
          selectedSkillIds={filterState.skillIds}
          selectedJurisdictionIds={filterState.jurisdictionIds}
          allCountrySelected={filterState.allCountry}
          jurisdictionsLoaded={jurisdictionsLoaded}
          verifiedOnly={verifiedOnly}
          onTypeChange={(nextType) => {
            setHeroCollapsed(true);
            setTypeFilter(nextType);
            const shouldApply = hasFilterSelections || verifiedOnly || nextType !== "FEATURED";
            if (!shouldApply) {
              if (filterState.page !== 1) {
                filterActions.setPage(1);
              }
              return;
            }
            if (filterState.page !== 1) {
              filterActions.setPage(1);
              return;
            }
            filterActions.applyFilters();
          }}
          onSkillToggle={handleSkillToggle}
          onJurisdictionToggle={handleJurisdictionToggle}
          onToggleAllCountry={handleToggleAllCountry}
          onToggleVerified={handleToggleVerified}
          onClearFilters={handleClearFilters}
          onResetType={() => setTypeFilter("ALL")}
          nameValue={filterState.name}
          onSearchClear={handleSearchClear}
          featured={filterState.featured}
          onToggleFeatured={filterActions.toggleFeatured}
          interpretSkillLabels={interpretSkillLabels}
          interpretJurisdictionLabels={interpretJurisdictionLabels}
        />
      }
      results={
        <PeopleSearchResults
          isLoading={isLoading}
          type={typeFilter}
          profiles={displayedProfiles}
          searchLabel={interpretSearchLabel}
          totalCount={totalCount}
          currentPage={usesRemotePagination ? pageForPagination : undefined}
          pageSize={usesRemotePagination ? DEFAULT_PAGE_SIZE : undefined}
          onPageChange={usesRemotePagination ? handlePageChange : undefined}
          onViewProfile={handleViewProfile}
          onContactWorker={handleContact}
          onClearFilters={handleClearFilters}
          onOpenFilters={() => {
            setHeroCollapsed(false);
            window.scrollTo({
              top: 0,
              behavior: "smooth",
            });
          }}
        />
      }
      mobileActions={null}
      shouldHideNavigation={ocultarEnMobile}
    />
  );
};

export default PeopleSearchPage;
